import React, { useEffect, useRef, useState } from 'react';
import { SpeakerPortrait } from './Art';
import { audio } from './audio';

const scenes = [
    { speaker: '', text: 'At dawn, three companions took the road to the Crown.\nNone of them knew what waited at its end.' },
    { speaker: 'Mira', text: 'One road. One company.\nLet’s bring everyone home.' },
    { speaker: 'Aster', text: 'Then we go together.\nNo one gets left behind.' },
    { speaker: '', text: 'Every legend begins with a choice.' }
];
export function ExpeditionIntro({ routeName, reducedMotion, onBegin, onBack }: { routeName: string; reducedMotion: boolean; onBegin: () => void; onBack: () => void }) {
    const [scene, setScene] = useState(0);
    const [letters, setLetters] = useState(0);
    const [leaving, setLeaving] = useState(false);
    const [autoPlay, setAutoPlay] = useState(true);
    const finished = useRef(false);
    const exitTimer = useRef<number | undefined>(undefined);
    const motionReduced = reducedMotion || !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const shot = scenes[scene];
    const complete = motionReduced || letters >= shot.text.length;
    useEffect(() => {
        setLetters(0);
        if (scene > 0) audio.play('story');
        if (motionReduced) return;
        const timer = window.setInterval(() => setLetters(n => Math.min(shot.text.length, n + 1)), 28);
        return () => window.clearInterval(timer);
    }, [scene, shot.text.length, motionReduced]);
    useEffect(() => () => window.clearTimeout(exitTimer.current), []);
    const begin = () => {
        if (finished.current) return;
        finished.current = true;
        setLeaving(true);
        audio.play('clear');
        exitTimer.current = window.setTimeout(onBegin, motionReduced ? 0 : 700);
    };
    const advance = () => {
        if (leaving) return;
        if (!complete) { setLetters(shot.text.length); return; }
        if (scene === scenes.length - 1) begin();
        else { setScene(n => n + 1); setLetters(0); }
    };
    const advanceRef = useRef(advance);
    advanceRef.current = advance;
    useEffect(() => {
        if (!complete || leaving || !autoPlay) return;
        const timer = window.setTimeout(() => advanceRef.current(), 3200);
        return () => window.clearTimeout(timer);
    }, [scene, complete, leaving, autoPlay]);
    const skip = () => {
        if (finished.current) return;
        finished.current = true;
        onBegin();
    };
    return <main className={`cinema-intro shot-${scene}${leaving ? ' cinema-leaving' : ''}`} aria-label="Expedition opening cinematic">
        <div className="cinema-landscape" aria-hidden="true"/>
        <div className="cinema-shade" aria-hidden="true"/>
        <div className="cinema-motes" aria-hidden="true">{Array.from({ length: 12 }, (_, i) => <i key={i} style={{ left: `${(i * 37 + 7) % 100}%`, top: `${(i * 19 + 15) % 80}%`, animationDelay: `${-i * 1.7}s` }}/>)}</div>
        <div className="cinema-letterbox top" aria-hidden="true"/><div className="cinema-letterbox bottom" aria-hidden="true"/>
        <nav className="cinema-nav" aria-label="Intro controls"><button disabled={leaving} onClick={onBack}>← Routes</button><button disabled={leaving} onClick={skip}>Skip intro ↗</button></nav>
        <div className="cinema-location"><span>PROLOGUE · {routeName}</span><h1>Before the first step.</h1><p>Emberwake Valley · Dawn</p></div>
        <div className="cinema-shot" key={scene}>
            {shot.speaker && <div className="cinema-portrait"><SpeakerPortrait speaker={shot.speaker}/></div>}
            {scene === 3 && <div className="cinema-title"><span>A COMPANY. A CROWN. A CHOICE.</span><h2>APORIA<br/>CROWN</h2><p>{routeName}</p></div>}
            <section className={`cinema-dialogue${shot.speaker ? ' spoken' : ' narration'}`} aria-label={shot.speaker ? `${shot.speaker} speaks` : 'Narration'}>
                {shot.speaker && <h2>{shot.speaker}<small>{shot.speaker === 'Mira' ? 'A promise for the road' : 'The first step'}</small></h2>}
                <p><span className="cinema-accessible-text">{shot.text}</span><span aria-hidden="true">{motionReduced ? shot.text : shot.text.slice(0, letters)}{!complete && <i className="cinema-cursor"/>}</span></p>
            </section>
        </div>
        <footer className="cinema-footer"><span>{String(scene + 1).padStart(2, '0')} <i/> 04</span><button autoFocus disabled={leaving} onClick={advance}>{scene === 3 ? 'Begin the journey' : complete ? 'Continue' : 'Reveal dialogue'} <b>▸</b></button><button className="cinema-auto" disabled={leaving} aria-pressed={autoPlay} onClick={() => setAutoPlay(value => !value)}>{autoPlay ? 'Ⅱ Auto on' : '▷ Auto off'}</button></footer>
        <div className="cinema-fade" aria-hidden="true"/>
    </main>;
}
